import { useState, useRef, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { updateIssue, optimisticUpdateIssueStatus } from '../store/slices/issueSlice'
import { fetchProjectStatuses, updateStatus, deleteStatus, reorderStatuses } from '../store/slices/boardStatusSlice'
import { fetchLabels } from '../store/slices/labelSlice'
import { usePermissions, Permission } from '../hooks/usePermissions'
import PermissionGate from './PermissionGate'
import BoardFilters from './BoardFilters'
import { Issue, IssueStatus, BoardStatus, BoardFilterState } from '@/types'
import { applyFilters, initialFilterState } from '../utils/filterUtils'
import IssueCard from './IssueCard'
import IssueDetailModal from './IssueDetailModal'
import QuickCreateIssueModal from './QuickCreateIssueModal'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import {
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

interface KanbanBoardProps {
  issues: Issue[]
  projectId: string
  onCreateStatus?: () => void
}


interface DroppableColumnProps {
  id: string
  title: string
  children: React.ReactNode
  issueCount: number
  status?: BoardStatus
  canReorderBoard?: boolean
  canManageBoardStatus?: boolean
  projectId: string
  sortableId?: string // 如果提供，則啟用拖曳功能
}

// 底部拖放區域組件
const DropZone = ({ statusId }: { statusId: string }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${statusId}-bottom`,
  })

  return (
    <div
      ref={setNodeRef}
      className={`h-full min-h-[40px] transition-colors duration-200 rounded-lg ${
        isOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : 'bg-transparent'
      }`}
    />
  )
}

const DroppableColumn = ({ 
  id, 
  title, 
  children, 
  issueCount, 
  status,
  canReorderBoard,
  canManageBoardStatus,
  projectId,
  sortableId
}: DroppableColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
  })
  
  // 總是調用 useSortable hook，但只在有 sortableId 時使用結果
  const sortableHookResult = useSortable({ 
    id: sortableId || 'dummy-id',
    data: {
      type: 'column',
      status: status
    }
  })
  
  // 如果沒有 sortableId，使用默認值
  const {
    attributes: sortableAttributes,
    listeners: sortableListeners,
    setNodeRef: setSortableNodeRef,
    transform: sortableTransform,
    transition: sortableTransition,
    isDragging: isSortableDragging,
  } = sortableId ? sortableHookResult : {
    attributes: {},
    listeners: {},
    setNodeRef: () => {},
    transform: null,
    transition: undefined,
    isDragging: false,
  }
  const dispatch = useAppDispatch()
  const { statuses } = useAppSelector(state => state.boardStatus)
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(title)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [moveToStatusId, setMoveToStatusId] = useState('')

  // 當 title 變化時，更新 editName
  useEffect(() => {
    setEditName(title)
  }, [title])

  const handleRename = async () => {
    if (status && editName.trim() && editName !== title) {
      try {
        console.log('正在重新命名狀態:', { statusId: status.id, oldName: title, newName: editName.trim() })
        const result = await dispatch(updateStatus({
          projectId,
          statusId: status.id,
          updateData: { name: editName.trim() }
        })).unwrap()
        console.log('重新命名成功，返回結果:', result)
        setIsEditing(false)
        // 強制重新載入狀態，確保同步
        dispatch(fetchProjectStatuses(projectId))
      } catch (error) {
        console.error('重新命名失敗:', error)
        setEditName(title) // 恢復原名稱
        setIsEditing(false)
      }
    } else {
      setEditName(title)
      setIsEditing(false)
    }
  }

  const handleDelete = async () => {
    if (status) {
      try {
        await dispatch(deleteStatus({
          projectId,
          statusId: status.id,
          moveToStatusId: moveToStatusId || undefined
        })).unwrap()
        setShowDeleteDialog(false)
      } catch (error: any) {
        console.error('刪除狀態失敗:', error)
        if (error.error === 'ISSUES_EXIST') {
          // 如果有議題存在，保持對話框開啟讓用戶選擇移動目標
          return
        }
        setShowDeleteDialog(false)
      }
    }
  }

  const availableStatuses = statuses.filter(s => s.id !== status?.id)

  // 組合 refs
  const setRefs = (node: HTMLDivElement | null) => {
    setNodeRef(node)
    if (sortableId && setSortableNodeRef) {
      setSortableNodeRef(node)
    }
  }

  // 計算拖曳樣式
  const sortableStyle = sortableTransform ? {
    transform: CSS.Transform.toString(sortableTransform),
    transition: sortableTransition,
  } : {}

  return (
    <div 
      className={`flex-shrink-0 w-72 sm:w-80 md:w-72 ${isSortableDragging ? 'opacity-50' : ''}`}
      style={sortableStyle}
      {...(sortableId ? sortableAttributes : {})}
    >
      <div 
        ref={setRefs}
        className={`bg-gray-100 rounded-lg p-3 md:p-4 border border-gray-200 shadow-sm ${isOver ? 'bg-blue-50 ring-2 ring-blue-300' : ''} ${isSortableDragging ? 'cursor-grabbing' : sortableId && (canReorderBoard ?? false) ? 'cursor-grab' : ''}`}
      >
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {isEditing ? (
              <div className="flex items-center space-x-2 flex-1">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={handleRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRename()
                    if (e.key === 'Escape') {
                      setEditName(title)
                      setIsEditing(false)
                    }
                  }}
                  className="flex-1 text-sm font-medium bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <button
                  onClick={handleRename}
                  className="p-1 text-green-600 hover:text-green-700"
                >
                  <CheckIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setEditName(title)
                    setIsEditing(false)
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div 
                className={`flex-1 ${sortableId && (canReorderBoard ?? false) ? 'cursor-grab active:cursor-grabbing' : ''}`}
                {...(sortableId && (canReorderBoard ?? false) ? sortableListeners : {})}
                title={sortableId && (canReorderBoard ?? false) ? '拖曳來重新排序狀態' : undefined}
              >
                <h3 className="font-medium text-gray-900 text-sm md:text-base truncate">
                  {title}
                </h3>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full">
              {issueCount}
            </span>
            
            {/* 三個點菜單 (需要管理權限且有狀態資料) */}
            {canManageBoardStatus && status && (
              <Menu as="div" className="relative">
                <Menu.Button className="p-1 rounded-md hover:bg-gray-200">
                  <EllipsisVerticalIcon className="w-4 h-4 text-gray-500" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => setIsEditing(true)}
                            className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700 w-full text-left`}
                          >
                            <PencilIcon className="w-4 h-4 mr-2" />
                            重新命名
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => setShowDeleteDialog(true)}
                            className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-red-600 w-full text-left`}
                          >
                            <TrashIcon className="w-4 h-4 mr-2" />
                            刪除狀態
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
          </div>
        </div>
        {children}
        
        {/* 刪除確認對話框 */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                刪除狀態「{title}」
              </h3>
              
              {/* 檢查是否有議題需要移動 */}
              {issueCount > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    此狀態下還有 {issueCount} 個議題，請選擇要移動到的狀態：
                  </p>
                  <select
                    value={moveToStatusId}
                    onChange={(e) => setMoveToStatusId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">選擇目標狀態</option>
                    {availableStatuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="text-sm text-gray-600 mb-4">
                  確定要刪除此狀態嗎？此操作無法復原。
                </p>
              )}
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowDeleteDialog(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  disabled={issueCount > 0 && !moveToStatusId}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md"
                >
                  刪除
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const KanbanBoard = ({ issues, projectId, onCreateStatus }: KanbanBoardProps) => {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createModalStatus, setCreateModalStatus] = useState<{ statusId: string; statusName: string } | null>(null)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [clientWidth, setClientWidth] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 })
  const [showScrollHint, setShowScrollHint] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [filters, setFilters] = useState<BoardFilterState>(initialFilterState)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollbarRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const { statuses } = useAppSelector(state => state.boardStatus)
  const { user } = useAppSelector(state => state.auth)
  
  // 套用篩選後的議題列表
  const filteredIssues = useMemo(() => {
    if (!user) return issues
    return applyFilters(issues, filters, user)
  }, [issues, filters, user])
  
  // 使用專案權限取代簡單的 isAdmin 檢查
  const { hasPermission } = usePermissions(projectId)
  
  // 檢查看板管理相關權限
  const canManageBoardStatus = hasPermission(Permission.MANAGE_BOARD_STATUS)
  const canReorderBoardStatus = hasPermission(Permission.REORDER_BOARD_STATUS)
  
  // 獲取專案的自定義狀態
  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectStatuses(projectId))
    }
  }, [dispatch, projectId])

  // 載入標籤數據
  useEffect(() => {
    dispatch(fetchLabels({}))
  }, [dispatch])

  // 計算要顯示的欄位配置
  const columnConfig = statuses.length > 0 
    ? statuses.map(status => ({
        id: status.id,
        title: status.name,
        status: status.id,
        color: status.color
      }))
    : [
        { id: 'TODO', title: '待辦', status: IssueStatus.TODO, color: '#6b7280' },
        { id: 'IN_PROGRESS', title: '進行中', status: IssueStatus.IN_PROGRESS, color: '#3b82f6' },
        { id: 'IN_REVIEW', title: '待審核', status: IssueStatus.IN_REVIEW, color: '#f59e0b' },
        { id: 'DONE', title: '完成', status: IssueStatus.DONE, color: '#10b981' },
      ]

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    // 手機版時使用更寬鬆的觸控感應器
    useSensor(TouchSensor, {
      activationConstraint: isMobile ? {
        delay: 500,
        tolerance: 15,
      } : {
        delay: 300,
        tolerance: 8,
      },
    })
  )

  // 檢測是否為手機版或 DevTools 模擬
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth
      const isDevTools = window.navigator.userAgent.includes('Chrome') && width < 768
      setIsMobile(width < 768)
      
      // 如果是 DevTools 模擬器，強制啟用滾動
      if (isDevTools && scrollContainerRef.current) {
        const container = scrollContainerRef.current
        container.style.touchAction = 'auto'
        container.style.overflowX = 'scroll'
        container.style.cursor = 'grab'
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // 自動隱藏滾動提示
  useEffect(() => {
    if (showScrollHint) {
      const timer = setTimeout(() => {
        setShowScrollHint(false)
      }, 5000) // 5秒後自動隱藏
      
      return () => clearTimeout(timer)
    }
  }, [showScrollHint])

  // 更新滾動狀態
  useEffect(() => {
    const updateScrollInfo = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
        setScrollLeft(scrollLeft)
        setScrollWidth(scrollWidth)
        setClientWidth(clientWidth)
        
        // 用戶開始滾動後隱藏提示
        if (scrollLeft > 0 && showScrollHint) {
          setShowScrollHint(false)
        }
      }
    }

    const container = scrollContainerRef.current
    if (container) {
      updateScrollInfo()
      container.addEventListener('scroll', updateScrollInfo)
      
      // 添加觸控事件處理
      const handleTouchStart = () => {
        if (showScrollHint) {
          setShowScrollHint(false)
        }
      }
      
      container.addEventListener('touchstart', handleTouchStart)
      
      // 監聽窗口大小變化
      const resizeObserver = new ResizeObserver(updateScrollInfo)
      resizeObserver.observe(container)
      
      return () => {
        container.removeEventListener('scroll', updateScrollInfo)
        container.removeEventListener('touchstart', handleTouchStart)
        resizeObserver.disconnect()
      }
    }
  }, [issues, showScrollHint])

  // 處理固定滾動條點擊
  const handleScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current || isDragging) return
    
    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    const newScrollLeft = percentage * (scrollWidth - clientWidth)
    
    scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' })
  }

  // 滾動條拖拽開始
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    setIsDragging(true)
    setDragStart({
      x: e.clientX,
      scrollLeft: scrollLeft
    })
  }

  // 滾動條拖拽進行中
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current || !scrollbarRef.current) return

    e.preventDefault()
    
    const rect = scrollbarRef.current.getBoundingClientRect()
    const deltaX = e.clientX - dragStart.x
    const deltaPercentage = deltaX / rect.width
    // 增加滾動速度倍數，讓拖拽響應更快
    const deltaScroll = deltaPercentage * (scrollWidth - clientWidth) * 2.5
    const newScrollLeft = Math.max(0, Math.min(scrollWidth - clientWidth, dragStart.scrollLeft + deltaScroll))
    
    // 直接設置滾動位置，使用更高效的滾動方式
    scrollContainerRef.current.scrollLeft = newScrollLeft
  }

  // 滾動條拖拽結束
  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // 添加全局滑鼠事件監聽
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false })
      document.addEventListener('mouseup', handleMouseUp)
      document.body.classList.add('dragging-scrollbar')
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.classList.remove('dragging-scrollbar')
      }
    }
  }, [isDragging, dragStart, scrollWidth, clientWidth])

  // 計算滾動條滑塊的位置和大小
  const scrollbarThumbWidth = scrollWidth > clientWidth ? (clientWidth / scrollWidth) * 100 : 100
  const scrollbarThumbLeft = scrollWidth > clientWidth ? (scrollLeft / (scrollWidth - clientWidth)) * (100 - scrollbarThumbWidth) : 0

  const handleDragStart = (event: DragStartEvent) => {
    console.log('Drag started:', event.active.id)
    setActiveId(event.active.id as string)
  }

  const handleColumnReorder = async (activeColumnId: string, overId: string) => {
    if (!activeColumnId.startsWith('column-')) {
      return
    }

    console.log('狀態拖曳事件:', { activeColumnId, overId })

    // 找到拖曳的欄位
    const activeIndex = columnConfig.findIndex(col => `column-${col.id}` === activeColumnId)
    if (activeIndex === -1) {
      console.log('找不到拖曳的欄位:', activeColumnId)
      return
    }

    let overIndex = -1
    
    // 嘗試查找目標欄位 - 可能是另一個 column、狀態 ID 或議題 ID
    if (overId.startsWith('column-')) {
      // 拖曳到另一個欄位標題
      overIndex = columnConfig.findIndex(col => `column-${col.id}` === overId)
    } else {
      // 先嘗試根據狀態 ID 查找欄位
      overIndex = columnConfig.findIndex(col => col.status === overId)
      
      // 如果找不到，可能是拖曳到議題上，需要找到該議題所屬的狀態欄位
      if (overIndex === -1) {
        const overIssue = issues.find(issue => issue.id === overId)
        if (overIssue) {
          // 根據議題的狀態找到對應的欄位
          const issueStatus = statuses.length > 0 ? overIssue.customStatus || overIssue.status : overIssue.status
          overIndex = columnConfig.findIndex(col => col.status === issueStatus)
          console.log('透過議題找到目標欄位:', { issueId: overId, issueStatus, overIndex })
        }
      }
    }

    if (overIndex === -1 || activeIndex === overIndex) {
      console.log('無效的目標位置:', { overId, overIndex, activeIndex })
      return
    }

    console.log('開始重新排序:', { activeIndex, overIndex })

    // 重新排列 columnConfig
    const reorderedColumns = arrayMove(columnConfig, activeIndex, overIndex)
    
    // 提取狀態 ID 順序
    const statusIds = reorderedColumns.map(col => col.status)

    try {
      // 調用 Redux action 更新後端
      await dispatch(reorderStatuses({ projectId, statusIds })).unwrap()
      console.log('狀態順序更新成功')
    } catch (error) {
      console.error('狀態順序更新失敗:', error)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    
    setActiveId(null)
    
    if (!over) {
      console.warn('拖曳到無效區域，取消操作')
      return
    }

    const activeId = active.id as string
    
    // 檢查是否為狀態欄位拖曳（需要重新排序權限）
    if (activeId.startsWith('column-') && canReorderBoardStatus) {
      console.log('檢測到狀態欄位拖曳:', { activeId, overId: over.id })
      handleColumnReorder(activeId, over.id as string)
      return
    }

    // 原有的議題拖曳邏輯
    const draggedIssueId = activeId
    const draggedIssue = issues.find(i => i.id === draggedIssueId)
    
    if (!draggedIssue) {
      console.error('找不到被拖曳的議題:', draggedIssueId)
      return
    }

    // 檢查是否拖放到另一個議題上（用於排序）
    const overIssue = issues.find(i => i.id === over.id)
    
    if (overIssue) {
      // 拖放到另一個議題上，進行排序
      handleIssueReorder(draggedIssue, overIssue)
    } else {
      // 檢查是否拖放到底部區域
      const overId = over.id as string
      if (overId.endsWith('-bottom')) {
        // 拖放到底部區域，移動到該狀態的最後位置
        const statusId = overId.replace('-bottom', '')
        handleMoveToBottom(draggedIssue, statusId)
      } else {
        // 拖放到欄位容器上
        const newStatusId = overId

        // 驗證 newStatusId 是否為有效的狀態值
        const isValidStatus = statuses.length > 0
          ? statuses.some(s => s.id === newStatusId)
          : ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE'].includes(newStatusId)
        
        if (!isValidStatus) {
          console.error('無效的狀態值:', newStatusId)
          return
        }

        // 即使狀態相同，如果是拖放到空白區域，也允許（可能是想放到列表末尾）
        handleStatusChange(draggedIssue, newStatusId)
      }
    }
  }

  const handleMoveToBottom = (draggedIssue: Issue, targetStatusId: string) => {
    console.log('移動到底部:', { issueId: draggedIssue.id, targetStatus: targetStatusId })

    // 獲取目標狀態的所有議題（排除當前拖曳的議題和已封存的議題）
    const targetStatusIssues = issues
      .filter(issue => {
        if (issue.archived || issue.id === draggedIssue.id) return false
        const issueStatus = statuses.length > 0 ? issue.customStatus : issue.status
        return issueStatus === targetStatusId
      })
      .sort((a, b) => a.position - b.position)

    // 計算新位置（放在該狀態的最後）
    const newPosition = targetStatusIssues.length > 0 
      ? targetStatusIssues[targetStatusIssues.length - 1].position + 1000
      : 1000

    // 準備更新數據
    const updateData: any = { position: newPosition }
    const currentStatus = statuses.length > 0 ? draggedIssue.customStatus : draggedIssue.status
    
    // 如果狀態不同，需要更新狀態
    if (currentStatus !== targetStatusId) {
      if (statuses.length > 0) {
        updateData.customStatus = targetStatusId
      } else {
        updateData.status = targetStatusId
      }
    }

    console.log('移動到底部更新數據:', { 
      issueId: draggedIssue.id, 
      updateData,
      currentStatus,
      targetStatus: targetStatusId,
      newPosition 
    })

    // 立即進行樂觀更新
    const optimisticPayload: any = { 
      issueId: draggedIssue.id, 
      position: newPosition 
    }
    
    if (statuses.length > 0) {
      optimisticPayload.customStatus = targetStatusId
    } else {
      optimisticPayload.status = targetStatusId
    }
    
    dispatch(optimisticUpdateIssueStatus(optimisticPayload))
    
    // 發送 API 請求
    dispatch(updateIssue({ 
      issueId: draggedIssue.id, 
      updateData 
    })).unwrap()
      .then((result) => {
        console.log('移動到底部成功:', {
          issueId: draggedIssue.id,
          updatedIssue: result?.issue,
          newPosition: result?.issue?.position,
          newStatus: result?.issue?.status
        })
      })
      .catch((error) => {
        console.error('移動到底部失敗:', error)
        // 回滾
        const rollbackPayload: any = { 
          issueId: draggedIssue.id, 
          position: draggedIssue.position 
        }
        
        if (statuses.length > 0) {
          rollbackPayload.customStatus = draggedIssue.customStatus
        } else {
          rollbackPayload.status = draggedIssue.status
        }
        
        dispatch(optimisticUpdateIssueStatus(rollbackPayload))
      })
  }

  const handleIssueReorder = (draggedIssue: Issue, overIssue: Issue) => {
    if (draggedIssue.id === overIssue.id) return

    // 比較當前狀態（優先使用自定義狀態）
    const draggedCurrentStatus = statuses.length > 0 ? draggedIssue.customStatus : draggedIssue.status
    const overCurrentStatus = statuses.length > 0 ? overIssue.customStatus : overIssue.status
    const sameStatus = draggedCurrentStatus === overCurrentStatus
    
    // 獲取目標狀態的所有議題並按位置排序，但排除被拖曳的議題以避免索引混亂
    const targetStatus = overCurrentStatus
    const targetStatusIssues = issues
      .filter(issue => {
        if (issue.archived) return false
        const issueStatus = statuses.length > 0 ? issue.customStatus : issue.status
        return issueStatus === targetStatus && issue.id !== draggedIssue.id
      })
      .sort((a, b) => a.position - b.position)

    // 找到拖放目標的索引（在排除拖曳議題後的列表中）
    const overIndex = targetStatusIssues.findIndex(issue => issue.id === overIssue.id)
    
    // 判斷拖曳方向，決定插入位置
    let newPosition: number
    let insertPosition: 'before' | 'after' = 'before' // 預設插入到目標之前
    
    // 如果是同欄位內的拖曳，根據原位置和目標位置判斷方向
    if (sameStatus) {
      const draggedOriginalIndex = issues
        .filter(issue => {
          if (issue.archived) return false
          const issueStatus = statuses.length > 0 ? issue.customStatus : issue.status
          return issueStatus === targetStatus
        })
        .sort((a, b) => a.position - b.position)
        .findIndex(issue => issue.id === draggedIssue.id)
      
      const overOriginalIndex = issues
        .filter(issue => {
          if (issue.archived) return false
          const issueStatus = statuses.length > 0 ? issue.customStatus : issue.status
          return issueStatus === targetStatus
        })
        .sort((a, b) => a.position - b.position)
        .findIndex(issue => issue.id === overIssue.id)
      
      // 如果是向下拖曳（從較小索引拖到較大索引），插入到目標之後
      if (draggedOriginalIndex < overOriginalIndex) {
        insertPosition = 'after'
      }
    }
    
    // 根據插入位置計算新位置
    if (insertPosition === 'before') {
      // 插入到 overIssue 之前
      if (overIndex === 0) {
        // 插入到第一個位置
        newPosition = targetStatusIssues[0].position - 1000
      } else {
        // 插入到前一個議題和 overIssue 之間
        const prevPosition = targetStatusIssues[overIndex - 1].position
        const nextPosition = targetStatusIssues[overIndex].position
        
        if (nextPosition - prevPosition > 1) {
          newPosition = (prevPosition + nextPosition) / 2
        } else {
          newPosition = prevPosition + 500
        }
      }
    } else {
      // 插入到 overIssue 之後
      if (overIndex === targetStatusIssues.length - 1) {
        // 插入到最後一個位置
        newPosition = targetStatusIssues[overIndex].position + 1000
      } else {
        // 插入到 overIssue 和下一個議題之間
        const currentPosition = targetStatusIssues[overIndex].position
        const nextPosition = targetStatusIssues[overIndex + 1].position
        
        if (nextPosition - currentPosition > 1) {
          newPosition = (currentPosition + nextPosition) / 2
        } else {
          newPosition = currentPosition + 500
        }
      }
    }

    console.log('重新排序:', {
      draggedIssue: { id: draggedIssue.id, title: draggedIssue.title, currentPosition: draggedIssue.position },
      overIssue: { id: overIssue.id, title: overIssue.title, position: overIssue.position },
      targetStatusIssues: targetStatusIssues.map(issue => ({ id: issue.id, title: issue.title, position: issue.position })),
      overIndex,
      newStatus: targetStatus,
      newPosition,
      sameStatus
    })

    // 更新議題狀態和位置
    const updateData: any = { position: newPosition }
    if (!sameStatus) {
      // 根據是否有自定義狀態來決定更新哪個欄位
      if (statuses.length > 0) {
        updateData.customStatus = targetStatus
      } else {
        updateData.status = targetStatus
      }
    }

    // 立即進行樂觀更新
    const optimisticUpdatePayload: any = { 
      issueId: draggedIssue.id, 
      position: newPosition 
    }
    
    if (statuses.length > 0) {
      optimisticUpdatePayload.customStatus = targetStatus
    } else {
      optimisticUpdatePayload.status = targetStatus
    }
    
    dispatch(optimisticUpdateIssueStatus(optimisticUpdatePayload))
    
    // 發送 API 請求
    dispatch(updateIssue({ 
      issueId: draggedIssue.id, 
      updateData 
    })).unwrap()
      .then((result) => {
        console.log('重新排序成功:', {
          issueId: draggedIssue.id,
          updatedIssue: result?.issue,
          newPosition: result?.issue?.position,
          newStatus: result?.issue?.status
        })
      })
      .catch((error) => {
        console.error('重新排序失敗:', error)
        console.error('失敗的更新數據:', updateData)
        // 回滾
        dispatch(optimisticUpdateIssueStatus({ 
          issueId: draggedIssue.id, 
          status: draggedIssue.status,
          position: draggedIssue.position 
        }))
      })
  }

  const handleStatusChange = (draggedIssue: Issue, newStatusId: string) => {
    // 獲取當前議題的狀態
    const currentStatus = statuses.length > 0 ? draggedIssue.customStatus : draggedIssue.status
    
    // 如果狀態相同，檢查是否真的需要移動位置
    if (currentStatus === newStatusId) {
      // 獲取同狀態的所有議題（排除當前拖曳的議題和已封存的議題）
      const sameStatusIssues = issues
        .filter(issue => {
          if (issue.archived) return false
          const issueStatus = statuses.length > 0 ? issue.customStatus : issue.status
          return issueStatus === newStatusId && issue.id !== draggedIssue.id
        })
        .sort((a, b) => a.position - b.position)

      // 如果沒有其他議題，或者已經在最後位置，不需要更新
      if (sameStatusIssues.length === 0) {
        console.log('同狀態且已在正確位置，無需更新')
        return
      }

      // 移動到該狀態的最後
      const newPosition = sameStatusIssues[sameStatusIssues.length - 1].position + 1000
      
      console.log('同狀態內移動到末尾:', { 
        issueId: draggedIssue.id, 
        currentPosition: draggedIssue.position,
        newPosition 
      })

      // 立即進行樂觀更新
      const optimisticPayload: any = { 
        issueId: draggedIssue.id, 
        position: newPosition 
      }
      
      if (statuses.length > 0) {
        optimisticPayload.customStatus = newStatusId
      } else {
        optimisticPayload.status = newStatusId
      }
      
      dispatch(optimisticUpdateIssueStatus(optimisticPayload))
      
      // 發送 API 請求
      dispatch(updateIssue({ 
        issueId: draggedIssue.id, 
        updateData: { position: newPosition }
      })).unwrap()
        .then((result) => {
          console.log('同狀態移動成功:', {
            issueId: draggedIssue.id,
            updatedIssue: result?.issue,
            newPosition: result?.issue?.position
          })
        })
        .catch((error) => {
          console.error('同狀態移動失敗:', error)
          // 回滾
          dispatch(optimisticUpdateIssueStatus({ 
            issueId: draggedIssue.id, 
            status: draggedIssue.status,
            position: draggedIssue.position 
          }))
        })
      return
    }

    // 不同狀態的處理
    const targetStatusIssues = issues
      .filter(issue => {
        if (issue.archived) return false
        const issueStatus = statuses.length > 0 ? issue.customStatus : issue.status
        return issueStatus === newStatusId
      })
      .sort((a, b) => a.position - b.position)

    // 計算新位置（放在該狀態的最後）
    const newPosition = targetStatusIssues.length > 0 
      ? targetStatusIssues[targetStatusIssues.length - 1].position + 1000
      : 1000

    console.log('狀態變更:', { 
      issueId: draggedIssue.id, 
      from: currentStatus, 
      to: newStatusId,
      newPosition 
    })

    // 立即進行樂觀更新
    const optimisticPayloadDiff: any = { 
      issueId: draggedIssue.id, 
      position: newPosition 
    }
    
    if (statuses.length > 0) {
      optimisticPayloadDiff.customStatus = newStatusId
    } else {
      optimisticPayloadDiff.status = newStatusId
    }
    
    dispatch(optimisticUpdateIssueStatus(optimisticPayloadDiff))
    
    // 發送 API 請求
    const apiUpdateData: any = { position: newPosition }
    if (statuses.length > 0) {
      apiUpdateData.customStatus = newStatusId
    } else {
      apiUpdateData.status = newStatusId
    }
    
    dispatch(updateIssue({ 
      issueId: draggedIssue.id, 
      updateData: apiUpdateData
    })).unwrap()
      .then((result) => {
        console.log('狀態變更成功:', {
          issueId: draggedIssue.id,
          updatedIssue: result?.issue,
          newPosition: result?.issue?.position,
          newStatus: result?.issue?.status
        })
      })
      .catch((error) => {
        console.error('狀態變更失敗:', error)
        console.error('失敗的更新數據:', apiUpdateData)
        // 回滾
        const rollbackPayload: any = { 
          issueId: draggedIssue.id, 
          position: draggedIssue.position 
        }
        
        if (statuses.length > 0) {
          rollbackPayload.customStatus = draggedIssue.customStatus
        } else {
          rollbackPayload.status = draggedIssue.status
        }
        
        dispatch(optimisticUpdateIssueStatus(rollbackPayload))
      })
  }

  const getIssuesByStatus = (statusId: string) => {
    const defaultCustomStatus = statuses.find(s => s.isDefault) ?? statuses[0] ?? null

    return filteredIssues
      .filter(issue => {
        // 過濾掉已封存的議題
        if (issue.archived) {
          return false
        }
        
        // 如果有自定義狀態系統
        if (statuses.length > 0) {
          // 優先檢查 customStatus
          if (issue.customStatus === statusId) {
            return true
          }

          // 如果沒有 customStatus，將其歸入預設自訂狀態
          if (!issue.customStatus && defaultCustomStatus && statusId === defaultCustomStatus.id) {
            return true
          }
          
          // 如果議題沒有 customStatus，需要將舊格式狀態映射到新格式
          if (!issue.customStatus && issue.status) {
            // 找到對應的自定義狀態
            const correspondingStatus = statuses.find(status => {
              // 根據狀態名稱進行映射
              const statusNameMap: Record<string, string> = {
                'TODO': '待辦',
                'IN_PROGRESS': '進行中', 
                'IN_REVIEW': '待審核',
                'DONE': '完成'
              }
              return status.name === statusNameMap[issue.status!]
            })
            
            return correspondingStatus?.id === statusId
          }
        } else {
          // 沒有自定義狀態時，使用原始邏輯
          return issue.status === statusId
        }
        
        return false
      })
      .sort((a, b) => a.position - b.position)
  }

  const handleIssueClick = (issue: Issue) => {
    setSelectedIssue(issue)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedIssue(null)
  }

  const handleCreateIssue = (statusId: string, statusName: string) => {
    setCreateModalStatus({ statusId, statusName })
    setIsCreateModalOpen(true)
  }

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false)
    setCreateModalStatus(null)
  }

  const activeIssue = activeId && !activeId.startsWith('column-') ? issues.find(issue => issue.id === activeId) : null
  const activeColumn = activeId && activeId.startsWith('column-') ? columnConfig.find(col => `column-${col.id}` === activeId) : null

  const showScrollbar = scrollWidth > clientWidth

  return (
    <>
      {/* 篩選器 */}
      <BoardFilters
        projectId={projectId}
        filters={filters}
        onFiltersChange={setFilters}
        issues={issues}
        filteredCount={filteredIssues.length}
        totalCount={issues.length}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* 為狀態欄位添加 SortableContext */}
        <SortableContext
          items={columnConfig.map(col => `column-${col.id}`)}
          strategy={horizontalListSortingStrategy}
        >
          <div 
            ref={scrollContainerRef}
            className="flex space-x-3 sm:space-x-4 md:space-x-3 overflow-x-auto scroll-container pb-4"
            style={{ 
              scrollbarWidth: 'none', // 隱藏 Firefox 滾動條
              msOverflowStyle: 'none', // 隱藏 IE/Edge 滾動條
              WebkitOverflowScrolling: 'touch',
              touchAction: window.innerWidth < 768 ? 'auto' : 'pan-x'
            }}
          >
            {columnConfig.map((column) => {
              const columnIssues = getIssuesByStatus(column.status)
              
              return (
                <DroppableColumn
                  key={column.id}
                  id={column.status}
                  title={column.title}
                  issueCount={columnIssues.length}
                  status={statuses.find(s => s.id === column.status)}
                  canReorderBoard={canReorderBoardStatus}
                  canManageBoardStatus={canManageBoardStatus}
                  projectId={projectId}
                  sortableId={canReorderBoardStatus ? `column-${column.id}` : undefined}
                >
                <SortableContext
                  items={columnIssues.map(issue => issue.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col">
                    <div className="space-y-3 sm:space-y-2 md:space-y-3">
                      {columnIssues.map((issue) => (
                        <IssueCard
                          key={issue.id}
                          issue={issue}
                          isDragging={activeId === issue.id}
                          onClick={() => handleIssueClick(issue)}
                          projectId={projectId}
                        />
                      ))}
                    </div>
                    
                    {/* 在卡片後方的建立按鈕 */}
                    <PermissionGate 
                      permission={Permission.CREATE_ISSUE} 
                      projectId={projectId}
                    >
                      <button
                        onClick={() => handleCreateIssue(column.status, column.title)}
                        className="w-full py-3 sm:py-2 mt-3 sm:mt-2 text-gray-600 bg-white hover:bg-gray-50 active:bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-md transition-all duration-200 flex items-center justify-center group shadow-sm hover:shadow"
                      >
                        <span className="text-lg mr-1 group-hover:scale-110 transition-transform">+</span>
                        <span className="text-sm font-medium">建立</span>
                      </button>
                    </PermissionGate>
                    
                    {/* 底部拖放區域 - 讓卡片可以拖曳到最底下，設定最小高度以保持拖拽功能 */}
                    <div className="min-h-[60px]">
                      <DropZone statusId={column.status} />
                    </div>
                  </div>
                </SortableContext>
              </DroppableColumn>
            )})}
            
            {/* 建立狀態按鈕 - 在所有欄位最右邊 */}
            <PermissionGate 
              permission={Permission.MANAGE_BOARD_STATUS} 
              projectId={projectId}
            >
              <div className="flex-shrink-0 flex items-start">
                <button
                  onClick={onCreateStatus}
                  className="w-14 h-14 sm:w-12 sm:h-12 bg-gray-100 hover:bg-gray-200 active:bg-gray-200 border border-gray-300 hover:border-gray-400 rounded-lg transition-all duration-200 flex items-center justify-center group shadow-sm hover:shadow-md"
                  title="建立狀態"
                >
                  <span className="text-2xl sm:text-xl text-gray-600 group-hover:text-gray-800 group-active:text-gray-800 transition-all">+</span>
                </button>
              </div>
            </PermissionGate>
          </div>
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          {activeIssue ? (
            <div className="rotate-6 scale-105">
              <IssueCard issue={activeIssue} isDragging={true} projectId={projectId} />
            </div>
          ) : activeColumn ? (
            <div className="opacity-90 bg-gray-100 rounded-lg p-4 w-72 border-2 border-dashed border-gray-400">
              <h3 className="font-medium text-gray-700">{activeColumn.title}</h3>
            </div>
          ) : null}
        </DragOverlay>

        <IssueDetailModal
          issue={selectedIssue}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </DndContext>

      {/* 固定在底部的滾動條 - 只在桌面版且需要滾動時顯示 */}
      {showScrollbar && scrollWidth > clientWidth && (
        <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-white border-t border-gray-200 z-40 hidden md:block" style={{ height: '13px' }}>
          <div 
            ref={scrollbarRef}
            className="h-full bg-gray-100 cursor-pointer relative mx-4 rounded"
            style={{ margin: '1px 16px' }}
            onClick={handleScrollbarClick}
          >
            <div
              className={`absolute top-0 bottom-0 rounded ${
                isDragging 
                  ? 'bg-blue-500 cursor-grabbing shadow-md' 
                  : 'bg-gray-500 hover:bg-gray-600 cursor-grab transition-all duration-100'
              }`}
              style={{
                left: `${scrollbarThumbLeft}%`,
                width: `${Math.max(scrollbarThumbWidth, 5)}%`, // 確保最小寬度
                transform: isDragging ? 'scaleY(1.1)' : 'scaleY(1)',
                transition: isDragging ? 'none' : 'all 0.1s ease-out'
              }}
              onMouseDown={handleThumbMouseDown}
            />
          </div>
        </div>
      )}

      {/* 手機版滾動提示 */}
      {showScrollHint && scrollWidth > clientWidth && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white px-4 py-2 rounded-lg text-sm z-50 md:hidden animate-pulse">
          👈 左右滑動查看更多欄位 👉
          <br />
          <span className="text-xs opacity-75">
            {window.navigator.userAgent.includes('Chrome') ? '(滑鼠拖拽滾動條)' : '(觸摸滑動)'}
          </span>
        </div>
      )}

      {/* 手機版滾動指示器 */}
      {scrollWidth > clientWidth && (
        <>
          {/* 左側指示器 */}
          {scrollLeft > 0 && (
            <div className="fixed left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center z-40 md:hidden">
              ←
            </div>
          )}
          
          {/* 右側指示器 */}
          {scrollLeft < scrollWidth - clientWidth - 10 && (
            <div className="fixed right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center z-40 md:hidden">
              →
            </div>
          )}
        </>
      )}

      {/* 快速建立議題模態框 */}
      <PermissionGate 
        permission={Permission.CREATE_ISSUE} 
        projectId={projectId}
      >
        {createModalStatus && (
          <QuickCreateIssueModal
            isOpen={isCreateModalOpen}
            onClose={handleCloseCreateModal}
            projectId={projectId}
            statusId={createModalStatus.statusId}
            statusName={createModalStatus.statusName}
          />
        )}
      </PermissionGate>
    </>
  )
}

export default KanbanBoard
